'use client'

import * as React from 'react'
import {
  ThemeProvider as NextThemesProvider,
  type ThemeProviderProps,
  useTheme,
} from 'next-themes'

/**
 * Mirror the theme SETTING + current locale into the SHARED `owlcat-theme` /
 * `owlcat-locale` cookies so the central accounts app inherits the shop's
 * light/dark choice and language on redirect (browser SSO). Mirrors portal's
 * ThemeCookieSync. We write the SETTING (`theme`: system|light|dark), NOT
 * resolvedTheme: a 'system' user must CLEAR the cookie so sibling apps also fall
 * back to the OS instead of being pinned to a concrete light/dark that stops
 * tracking prefers-color-scheme cross-app. Parent domain is env-driven
 * (NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN); host-only when unset (bare localhost dev),
 * Domain-scoped behind the reverse proxy for real cross-app inheritance. The
 * pre-hydration seed script in app/layout.tsx reads the cookie back into
 * localStorage['theme'] so next-themes hydrates in agreement (no reset, no flash).
 */
function OwlcatCookieSync() {
  const { theme } = useTheme()
  React.useEffect(() => {
    const parent = process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN?.trim()
    const host = window.location.hostname
    const onParent =
      !!parent && (host === parent || host.endsWith(`.${parent}`))
    const domain = onParent ? `; Domain=.${parent}` : ''
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    const attrs = `Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`

    if (theme === 'light' || theme === 'dark') {
      document.cookie = `owlcat-theme=${theme}; ${attrs}`
    } else if (theme === 'system') {
      document.cookie = `owlcat-theme=; Path=/; Max-Age=0; SameSite=Lax${domain}${secure}`
    }
    // Locale from the <html lang> the server layout stamps (this provider sits
    // OUTSIDE NextIntlClientProvider, so useLocale isn't reachable here).
    const lang = document.documentElement.lang
    if (/^(ru|en|cs|uk)$/.test(lang)) {
      document.cookie = `owlcat-locale=${lang}; ${attrs}`
    }
  }, [theme])
  return null
}

/**
 * Theme provider wrapper for next-themes
 * Provides light/dark mode theme switching with system preference detection
 *
 * @example
 * <ThemeProvider
 *   attribute="class"
 *   defaultTheme="light"
 *   enableSystem
 *   disableTransitionOnChange
 * >
 *   {children}
 * </ThemeProvider>
 */
export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider {...props}>
      <OwlcatCookieSync />
      {children}
    </NextThemesProvider>
  )
}
